import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import McpKeysPanel from '../components/McpKeysPanel';
import AnalyticsTimezoneNote from '../components/AnalyticsTimezoneNote';
import { fetchMcpKeys } from '../services/mcpKeysService';
import { fetchAgentUtterances } from '../services/intelligenceService';
import { ANALYTICS_TIMEZONE, wallTimeToInstant, instantToWallTime, formatInTimeZone } from '../utils/timezone';
import './IntelligencePage.css';

const TABS = [
  { id: 'setup', label: 'Setup' },
  { id: 'usage', label: 'Agent usage' },
];

const EXAMPLE_QUESTIONS = [
  "what was Austin's goggles experience the past few days?",
  'which resorts have people been skiing at this week?',
  'what shipped in the last two weeks?',
  'is the App Store build the same as the register says?',
];

const OUTCOME_LABELS = {
  answered: 'Answered',
  'error:answer': 'Error answering',
  'error:identity': 'Could not identify asker',
  rate_limited: 'Rate limited',
  forbidden: 'Refused: not @rekkie.com',
  'forbidden:dm': 'Refused: DM not allowed',
  empty: 'Empty question',
};

const BAR_COLOR = '#667eea';

/** YYYY-MM-DD in the analytics zone. */
const todayIn = (tz) => instantToWallTime(new Date(), tz).slice(0, 10);
const addDays = (day, n) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');

const fmtDuration = (ms) => (Number.isFinite(ms) ? `${(ms / 1000).toFixed(1)}s` : '—');
const fmtTime = (iso, tz) => formatInTimeZone(iso, tz, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtTokens = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));
const askerKey = (r) => r.email || r.slackUserId || 'unknown';
const fmtCount = (n) => (n || 0).toLocaleString('en-US');

/** Token use of one row: the four API counters and their sum. */
const tokensOf = (usage) => {
  const t = {
    input: Number(usage?.input_tokens) || 0,
    output: Number(usage?.output_tokens) || 0,
    cacheRead: Number(usage?.cache_read_input_tokens) || 0,
    cacheWrite: Number(usage?.cache_creation_input_tokens) || 0,
  };
  t.total = t.input + t.output + t.cacheRead + t.cacheWrite;
  return t;
};

const CHART_METRICS = [
  { id: 'count', label: 'Questions', unit: (v) => `${fmtCount(v)} question${v === 1 ? '' : 's'}` },
  { id: 'tokens', label: 'Tokens', unit: (v) => `${fmtCount(v)} tokens` },
];

const CopyBlock = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* the text stays selectable */
    }
  };
  return (
    <div className="ri-code">
      <pre>{value}</pre>
      <button type="button" className="ri-copy-btn" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
};

const SetupTab = () => {
  const [mcpUrl, setMcpUrl] = useState('');
  useEffect(() => {
    fetchMcpKeys().then((d) => setMcpUrl(d.mcpUrl || '')).catch(() => {});
  }, []);
  const url = mcpUrl || 'https://offboard-api-ifxcuf34kq-uc.a.run.app/mcp';

  return (
    <div className="ri-setup">
      <section className="ri-card">
        <h3>Offboard Agent in Slack</h3>
        <p>Mention <code>@Offboard Agent</code> in any channel it has been invited to (<code>/invite @Offboard Agent</code>). It replies in the thread, and a follow-up in the same thread keeps the earlier messages as context. Direct messages work for people on the allow list.</p>
        <ul className="ri-examples">
          {EXAMPLE_QUESTIONS.map((q) => <li key={q}>@Offboard Agent {q}</li>)}
        </ul>
        <p className="ri-muted">Answers come from the same tools as the MCP server below, for @rekkie.com Slack accounts. Ranges default to the last 7 days, capped at 31. Limit: 6 questions per minute per person.</p>
      </section>

      <section className="ri-card">
        <h3>Connect an MCP client</h3>
        <p>The REKKIE MCP server gives any MCP client (Claude, Claude Code, the Claude API, other agent SDKs) read access to per-user analytics, crashes, goggles logs, ski days, resorts, releases and the app stores. Any @rekkie.com account can connect.</p>

        <div className="ri-field-label">Server URL</div>
        <CopyBlock value={url} />

        <h4>claude.ai or Claude Desktop (custom connector)</h4>
        <ol>
          <li>Settings → Connectors → Add custom connector.</li>
          <li>Name it <code>REKKIE</code> and paste the server URL. Leave client id and secret empty.</li>
          <li>Click Connect, then Allow on the Offboard consent page.</li>
        </ol>

        <h4>Claude Code</h4>
        <p>With OAuth (then run <code>/mcp</code> in Claude Code to sign in through the Offboard consent page):</p>
        <CopyBlock value={`claude mcp add --transport http rekkie ${url}`} />
        <p>With an API key (create one below):</p>
        <CopyBlock value={`claude mcp add --transport http rekkie ${url} --header "Authorization: Bearer rk_..."`} />

        <h4>Claude API (MCP connector)</h4>
        <CopyBlock value={`mcp_servers: [{ type: "url", url: "${url}", name: "rekkie", authorization_token: "rk_..." }]`} />

        <h4>Local, from the repo</h4>
        <p className="ri-muted">Runs the server over stdio with your <code>serviceAccount.json</code>; no key needed.</p>
        <CopyBlock value="claude mcp add rekkie -- node backend/mcp/stdio.js" />
      </section>

      <section className="ri-card">
        <McpKeysPanel />
      </section>
    </div>
  );
};

const DayTooltip = ({ active, payload, label, metric }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ri-tooltip">
      <div className="ri-tooltip-day">{label}</div>
      <div>{metric.unit(payload[0].value)}</div>
    </div>
  );
};

const UtteranceRow = ({ row, tz, expanded, onToggle }) => {
  const tokens = row.usage ? tokensOf(row.usage) : null;
  return (
    <>
      <tr className={`ri-row ${expanded ? 'ri-row-open' : ''}`} onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onToggle(); }}>
        <td className="ri-nowrap">{fmtTime(row.createdAt, tz)}</td>
        <td className="ri-nowrap" title={row.email || row.slackUserId}>{row.name || row.email || row.slackUserId}</td>
        <td className="ri-nowrap">{row.kind === 'dm' ? 'DM' : row.channel}{row.threadTs ? ' · thread' : ''}</td>
        <td className="ri-question">{row.question || <span className="ri-muted">(empty)</span>}</td>
        <td className="ri-nowrap"><span className={`ri-outcome ri-outcome-${row.outcome === 'answered' ? 'ok' : String(row.outcome).startsWith('error') ? 'error' : 'other'}`}>{OUTCOME_LABELS[row.outcome] || row.outcome}</span></td>
        <td className="ri-num">{row.toolCalls.length || ''}</td>
        <td className="ri-num">{fmtDuration(row.durationMs)}</td>
        <td className="ri-num">{tokens ? fmtTokens(tokens.total) : '—'}</td>
      </tr>
      {expanded && (
        <tr className="ri-detail">
          <td colSpan={8}>
            {row.answer && (
              <>
                <div className="ri-field-label">Answer</div>
                <pre className="ri-answer">{row.answer}</pre>
              </>
            )}
            {row.toolCalls.length > 0 && (
              <>
                <div className="ri-field-label">Tool calls</div>
                <ol className="ri-tool-calls">
                  {row.toolCalls.map((c, i) => (
                    <li key={i}>
                      <code className={c.ok ? '' : 'ri-tool-failed'}>{c.name}</code>{!c.ok && ' (failed)'} <span className="ri-muted">{c.input}</span>
                    </li>
                  ))}
                </ol>
              </>
            )}
            <div className="ri-meta">
              {row.email && <span>{row.email}</span>}
              {row.historyCount > 0 && <span>{row.historyCount} earlier messages as context</span>}
              {row.stopReason && <span>stop: {row.stopReason}</span>}
              {row.truncated && <span>stopped early: {row.truncated}</span>}
            </div>
            {tokens && (
              <div className="ri-meta">
                <span>{fmtCount(tokens.total)} tokens over {row.usage.turns} model call{row.usage.turns === 1 ? '' : 's'}</span>
                <span>input {fmtCount(tokens.input)}</span>
                <span>cache read {fmtCount(tokens.cacheRead)}</span>
                <span>cache write {fmtCount(tokens.cacheWrite)}</span>
                <span>output {fmtCount(tokens.output)}</span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const UsageTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tz = ANALYTICS_TIMEZONE;
  const today = todayIn(tz);
  const [startDate, setStartDate] = useState(isDay(searchParams.get('start')) ? searchParams.get('start') : addDays(today, -7));
  const [endDate, setEndDate] = useState(isDay(searchParams.get('end')) ? searchParams.get('end') : today);
  const [asker, setAsker] = useState(searchParams.get('asker') || '');
  const [outcome, setOutcome] = useState(searchParams.get('outcome') || '');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [chartMetric, setChartMetric] = useState('count');
  const metric = CHART_METRICS.find((m) => m.id === chartMetric);

  const load = useCallback(async () => {
    const start = wallTimeToInstant(`${startDate}T00:00`, tz);
    const end = wallTimeToInstant(`${addDays(endDate, 1)}T00:00`, tz);
    if (!start || !end || start >= end) {
      setError('Start date must be on or before the end date.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await fetchAgentUtterances(start, end));
      setExpanded(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, tz]);

  useEffect(() => { load(); }, [load]);

  // Keep the filters in the URL so a view can be shared.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const set = (k, v) => (v ? next.set(k, v) : next.delete(k));
    set('start', startDate);
    set('end', endDate);
    set('asker', asker);
    set('outcome', outcome);
    set('q', search);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [startDate, endDate, asker, outcome, search, searchParams, setSearchParams]);

  const rows = useMemo(() => data?.utterances || [], [data]);
  const askers = data?.summary?.byAsker || [];
  const outcomes = data?.summary?.byOutcome || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (!asker || askerKey(r) === asker)
      && (!outcome || r.outcome === outcome)
      && (!q || String(r.question || '').toLowerCase().includes(q) || String(r.answer || '').toLowerCase().includes(q)));
  }, [rows, asker, outcome, search]);

  // One bar per calendar day in the range, zero-filled, over the filtered rows.
  const daily = useMemo(() => {
    const byDay = new Map();
    for (const r of filtered) {
      const day = instantToWallTime(r.createdAt, tz).slice(0, 10);
      const d = byDay.get(day) || { count: 0, tokens: 0 };
      d.count += 1;
      d.tokens += tokensOf(r.usage).total;
      byDay.set(day, d);
    }
    const out = [];
    for (let d = startDate; d <= endDate && out.length < 400; d = addDays(d, 1)) {
      out.push({ day: d, label: d.slice(5).replace('-', '/'), count: byDay.get(d)?.count || 0, tokens: byDay.get(d)?.tokens || 0 });
    }
    return out;
  }, [filtered, startDate, endDate, tz]);

  const stats = useMemo(() => {
    const answered = filtered.filter((r) => r.outcome === 'answered');
    const durations = answered.map((r) => r.durationMs).filter(Number.isFinite).sort((a, b) => a - b);
    const tools = new Map();
    for (const r of filtered) for (const c of r.toolCalls) tools.set(c.name, (tools.get(c.name) || 0) + 1);
    const people = new Map();
    const tokens = { total: 0, input: 0, output: 0, cacheRead: 0, cacheWrite: 0 };
    for (const r of filtered) {
      const t = tokensOf(r.usage);
      for (const k of Object.keys(tokens)) tokens[k] += t[k];
      const k = askerKey(r);
      const p = people.get(k) || { key: k, label: r.name || r.email || r.slackUserId, count: 0, tokens: 0 };
      p.count += 1;
      p.tokens += t.total;
      people.set(k, p);
    }
    return {
      total: filtered.length,
      answered: answered.length,
      errors: filtered.filter((r) => String(r.outcome).startsWith('error')).length,
      askers: people.size,
      median: durations.length ? durations[Math.floor(durations.length / 2)] : null,
      tokens,
      tokensPerAnswer: answered.length ? Math.round(answered.reduce((n, r) => n + tokensOf(r.usage).total, 0) / answered.length) : null,
      topTools: [...tools].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10),
      topAskers: [...people.values()].sort((a, b) => b.count - a.count).slice(0, 10),
    };
  }, [filtered]);

  const changeRange = (setter) => (e) => {
    setter(e.target.value);
    setAsker('');
    setOutcome('');
  };

  return (
    <div className="ri-usage">
      <div className="ri-controls">
        <input type="date" className="ri-input" value={startDate} max={endDate} onChange={changeRange(setStartDate)} />
        <span className="ri-muted">to</span>
        <input type="date" className="ri-input" value={endDate} min={startDate} onChange={changeRange(setEndDate)} />
        <select className="ri-input" value={asker} onChange={(e) => setAsker(e.target.value)}>
          <option value="">All askers</option>
          {askers.map((a) => <option key={a.email || a.slackUserId} value={a.email || a.slackUserId || 'unknown'}>{a.name || a.email || a.slackUserId} ({a.count})</option>)}
        </select>
        <select className="ri-input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
          <option value="">All outcomes</option>
          {outcomes.map((o) => <option key={o.outcome} value={o.outcome}>{OUTCOME_LABELS[o.outcome] || o.outcome} ({o.count})</option>)}
        </select>
        <input type="search" className="ri-input ri-search" placeholder="Search questions and answers" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button type="button" className="ri-btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {error && <div className="ri-error">{error}</div>}
      {data?.truncated && <div className="ri-warn">Showing the newest {data.maxRows} questions in this range; narrow the dates to see the rest.</div>}

      <div className="ri-stats">
        <div className="ri-stat"><div className="ri-stat-value">{stats.total}</div><div className="ri-stat-label">Questions</div></div>
        <div className="ri-stat"><div className="ri-stat-value">{stats.askers}</div><div className="ri-stat-label">People</div></div>
        <div className="ri-stat"><div className="ri-stat-value">{stats.answered}</div><div className="ri-stat-label">Answered</div></div>
        <div className="ri-stat"><div className="ri-stat-value">{stats.errors}</div><div className="ri-stat-label">Errors</div></div>
        <div className="ri-stat"><div className="ri-stat-value">{fmtDuration(stats.median)}</div><div className="ri-stat-label">Median answer time</div></div>
        <div className="ri-stat" title={`input ${fmtCount(stats.tokens.input)} · cache read ${fmtCount(stats.tokens.cacheRead)} · cache write ${fmtCount(stats.tokens.cacheWrite)} · output ${fmtCount(stats.tokens.output)}`}>
          <div className="ri-stat-value">{fmtTokens(stats.tokens.total)}</div>
          <div className="ri-stat-label">Tokens ({fmtTokens(stats.tokens.output)} output)</div>
        </div>
        <div className="ri-stat"><div className="ri-stat-value">{stats.tokensPerAnswer == null ? '—' : fmtTokens(stats.tokensPerAnswer)}</div><div className="ri-stat-label">Tokens per answer</div></div>
      </div>

      <section className="ri-card">
        <div className="ri-card-head">
          <h3>{metric.label} per day</h3>
          <div className="ri-tabs ri-tabs-small" role="tablist">
            {CHART_METRICS.map((m) => (
              <button key={m.id} type="button" role="tab" aria-selected={chartMetric === m.id} className={`ri-tab ${chartMetric === m.id ? 'ri-tab-active' : ''}`} onClick={() => setChartMetric(m.id)}>{m.label}</button>
            ))}
          </div>
        </div>
        <div className="ri-chart">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: chartMetric === 'tokens' ? 0 : -20 }}>
              <CartesianGrid stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#888', fontSize: 11 }} axisLine={{ stroke: '#333' }} tickLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fill: '#888', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={chartMetric === 'tokens' ? fmtTokens : fmtCount} width={chartMetric === 'tokens' ? 56 : 40} />
              <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={<DayTooltip metric={metric} />} />
              <Bar dataKey={chartMetric} fill={BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="ri-split">
        <section className="ri-card">
          <h3>Top askers</h3>
          {stats.topAskers.length === 0 ? <p className="ri-muted">None in this range.</p> : (
            <table className="ri-table ri-table-compact">
              <tbody>
                {stats.topAskers.map((a) => (
                  <tr key={a.key} className="ri-row" onClick={() => setAsker(asker === a.key ? '' : a.key)} role="button" tabIndex={0}>
                    <td>{a.label}</td><td className="ri-num">{a.count}</td><td className="ri-num ri-muted">{fmtTokens(a.tokens)} tok</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        <section className="ri-card">
          <h3>Top tools</h3>
          {stats.topTools.length === 0 ? <p className="ri-muted">None in this range.</p> : (
            <table className="ri-table ri-table-compact">
              <tbody>
                {stats.topTools.map((t) => <tr key={t.name}><td><code>{t.name}</code></td><td className="ri-num">{t.count}</td></tr>)}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="ri-card">
        <h3>Utterances</h3>
        {loading && !data ? <p className="ri-muted">Loading…</p> : filtered.length === 0 ? (
          <p className="ri-muted">No questions match.</p>
        ) : (
          <div className="ri-table-wrap">
            <table className="ri-table">
              <thead>
                <tr><th>Time</th><th>Asker</th><th>Where</th><th>Question</th><th>Outcome</th><th className="ri-num">Tools</th><th className="ri-num">Took</th><th className="ri-num">Tokens</th></tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <UtteranceRow key={r.id} row={r} tz={tz} expanded={expanded === r.id} onToggle={() => setExpanded(expanded === r.id ? null : r.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

/**
 * Tools → REKKIE Intelligence: how to reach the REKKIE MCP server and the
 * Offboard Slack agent, and what people have asked the agent.
 */
const IntelligencePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'setup';
  const selectTab = (id) => setSearchParams(id === 'setup' ? {} : { tab: id });

  return (
    <div className="ri-page">
      <div className="ri-header">
        <h2>REKKIE Intelligence</h2>
        <div className="ri-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`ri-tab ${tab === t.id ? 'ri-tab-active' : ''}`} onClick={() => selectTab(t.id)}>{t.label}</button>
          ))}
        </div>
      </div>
      {tab === 'setup' ? <SetupTab /> : <UsageTab />}
    </div>
  );
};

export default IntelligencePage;
